import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ListarPessoaModel } from '../models/listar-pessoa.model';
import { EnumSimNao } from '../../../core/enums/enum-simnao';
import { environment } from '../../../core/environments/environment';
import { lastValueFrom } from 'rxjs';
import { PessoaModel } from '../../../core/models/pessoa.model';
import { PlanoModel } from '../../gestaoPlano/models/plano.model';

@Injectable({
  providedIn: 'root',
})
export class GestaoPessoaService {
  constructor(private http: HttpClient, private Router: Router) {}
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    withCredentials: true,

  };

  /**
   * Busca todas as pessoas com base nos filtros informados.
   * @param filtros Um objeto com os filtros de busca.
   * @returns Uma Promise que resolve com um array de {@link ListarPessoaModel} contendo as pessoas encontradas.
   */
  async buscarPessoas(filtros: any): Promise<ListarPessoaModel[]> {
    try {
      return await lastValueFrom(
        this.http.post<ListarPessoaModel[]>(
          `${environment.apiPessoaAdmin}/findAll`,
          JSON.stringify(filtros),
          this.httpOptions
        )
      );
    } catch (error) {
      return [];
    }
  }

  /**
   * Busca uma pessoa pelo ID fornecido.
   * @param id O ID da pessoa a ser buscada.
   * @returns Uma Promise que resolve com um objeto {@link PessoaModel} se a pessoa for encontrada,
   * ou null se não for encontrada.
   * @throws Um erro se a requisição falhar.
   */

  async buscarPessoaPorId(id: number): Promise<PessoaModel | null> {
    try {
      return await lastValueFrom(
        this.http.get<PessoaModel>(
          `${environment.apiPessoaAdmin}/findById?id=${id}`,
          this.httpOptions
        )
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Busca a hierarquia de uma pessoa a partir do ID informado.
   * @param id O ID da pessoa a ser buscada.
   * @returns Uma Promise que resolve com um array de {@link ListarPessoaModel} contendo as pessoas encontradas.
   */
  async buscarHierarquiaPessoa(id: number): Promise<ListarPessoaModel[]> {
    return this.buscarPessoas({ id: id });
  }

  /**
   * Busca os planos associados a uma pessoa específica.
   * @param pessoaId O ID da pessoa cujos planos associados serão buscados.
   * @returns Uma Promise que resolve com um array de {@link PlanoModel} contendo os planos associados.
   * Retorna um array vazio se ocorrer um erro durante a busca.
   */

  async buscarPlanosAssociados(pessoaId: number): Promise<PlanoModel[]> {
    try {
      return await lastValueFrom(
        this.http.get<PlanoModel[]>(
          `${environment.apiPessoaAdmin}/findPlanos?id=${pessoaId}`,
          this.httpOptions
        )
      );
    } catch (error) {
      return [];
    }
  }

  /**
   * Salva a associação de planos com uma pessoa.
   * @param pessoaId O ID da pessoa que terá os planos associados.
   * @param planosId O array de IDs dos planos a serem associados.
   * @returns Uma Promise que resolve com um booleano indicando se a associação foi feita com sucesso.
   * Retorna false se ocorrer um erro durante a execução.
   */
  async salvarAssociarPlano(
    pessoaId: number,
    planosId: number[]
  ): Promise<boolean> {
    try {
      return await lastValueFrom(
        this.http.post<boolean>(
          `${environment.apiPessoaAdmin}/associarPlano`,
          JSON.stringify({ pessoaId, planosId }),
          this.httpOptions
        )
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Desvincula um plano de uma pessoa.
   * @param pessoaId O ID da pessoa que terá o plano desvinculado.
   * @param planosId O ID do plano a ser desvinculado.
   * @returns Uma Promise que resolve com um booleano indicando se a desassociação foi feita com sucesso.
   * Retorna false se ocorrer um erro durante a execução.
   */
  async desvincularPlano(pessoaId: number, planosId: number): Promise<boolean> {
    try {
      return await lastValueFrom(
        this.http.post<boolean>(
          `${environment.apiPessoaAdmin}/desvincularPlano`,
          JSON.stringify({ pessoaId, planosId }),
          this.httpOptions
        )
      );
    } catch (error) {
      return false;
    }
  }
}
